import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
    ArrowLeft,
    Clock,
    Plus,
    Loader2,
    ChevronLeft,
    ChevronRight,
    Trash2,
    Repeat,
    CalendarPlus,
    UserPlus,
    X,
    AlertCircle,
    CheckCircle2,
    Zap,
    Sparkles,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import GymTimezoneSelect from "@/components/coach/GymTimezoneSelect";
import { CATEGORIES, CATEGORY_LABELS } from "@/lib/workoutTaxonomy";
import {
    DAY_LABELS,
    DAY_LABELS_LONG,
    HALF_HOUR_TIMES,
    formatTimeDisplay,
    isoDate,
    startOfWeekMon,
    addDays,
    weekRangeLabel,
    jsDayToMonIdx,
} from "@/lib/schedule";
import { ModalShell } from "@/components/coach/schedule/ModalShell";
import {
    TemplateModal, CloseDayModal, SlotModal, PublishModal,
} from "@/components/coach/schedule/modals";
import SlotBookingsModal, { BulkRecurringAddModal } from "@/components/coach/schedule/SlotBookingsModal";
import RecentDeductionsPanel from "@/components/coach/schedule/DeductionsPanel";
import FloatingNowPill from "@/components/coach/schedule/FloatingNowPill";
import OpsTabs from "@/components/coach/OpsTabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---- "Now"-aware helpers (used by NOW pill, auto-scroll, walk-in floater) ----

/**
 * Convert a slot's date + start_time into a millisecond timestamp in the
 * coach's local tz. Schedule slots store HH:MM in coach-local time
 * (no tz suffix), so we hand-construct a Date.
 */
function slotStartMs(slot) {
    if (!slot?.date || !slot?.start_time) return 0;
    const [y, m, d] = slot.date.split("-").map(Number);
    const [hh, mm] = slot.start_time.split(":").map(Number);
    return new Date(y, m - 1, d, hh, mm, 0, 0).getTime();
}

/** True when a slot is "live" — between start and start+duration. */
function isSlotInProgress(slot, nowMs) {
    const start = slotStartMs(slot);
    if (!start) return false;
    const end = start + (slot.duration_minutes || 60) * 60_000;
    return nowMs >= start && nowMs < end;
}

/** True when slot starts within the next N minutes (NOW-soon indicator). */
function isSlotUpNext(slot, nowMs, withinMinutes = 30) {
    const start = slotStartMs(slot);
    if (!start) return false;
    return start > nowMs && start - nowMs <= withinMinutes * 60_000;
}

/**
 * Big, loud toast for session-deduction feedback. The user's old CRM
 * silently swallowed missed decrements — this is intentionally noisy so
 * coaches can't miss whether a session was actually burned. Branches:
 *   • credit / pack found    → green "✓ N session(s) deducted, X left"
 *   • nothing found          → red "⚠ NO SESSION DEDUCTED" (clear, not buried)
 */
function toastDeduction(athleteName, response) {
    const credit = response?.credit_used || response?.pack_used;
    const name = athleteName || "Athlete";
    if (credit) {
        const packLabel = credit.pack_name || credit.pack_label || "session pack";
        const remaining = typeof credit.remaining === "number" ? credit.remaining : null;
        const remainingText = remaining === null
            ? ""
            : remaining === 0
                ? " · LAST SESSION — pack now empty"
                : ` · ${remaining} left`;
        toast.success(`✓ ${name} checked in`, {
            description: `1 session deducted from ${packLabel}${remainingText}`,
            duration: 6000,
            // Tailwind className passthrough so coaches see GREEN bg
            className: "border-emerald-600 bg-emerald-950/90 text-emerald-100",
        });
    } else {
        toast.error(`⚠ ${name} checked in — NO SESSION DEDUCTED`, {
            description: "No active pack or credit on file. Athlete is on subscription or needs to purchase a pack.",
            duration: 10000,
            className: "border-rose-600 bg-rose-950/95 text-rose-100",
        });
    }
}

const emptyTemplate = {
    label: "Small Group",
    days_of_week: [],
    start_times: [],
    duration_minutes: 75,
    capacity: 8,
    cancel_window_hours: 12,
    category: "",
    active: true,
};

const emptySlot = {
    date: isoDate(new Date()),
    start_time: "06:00",
    duration_minutes: 75,
    capacity: 8,
    cancel_window_hours: 12,
    category: "",
    label: "",
    notes: "",
};

export default function CoachSchedule() {
    const { user, loading } = useAuth();
    const navigate = useNavigate();

    const [weekStart, setWeekStart] = useState(() => startOfWeekMon(new Date()));
    const [templates, setTemplates] = useState([]);
    const [slots, setSlots] = useState([]);
    const [closures, setClosures] = useState([]); // [{date, note}]
    const [members, setMembers] = useState([]); // [{user_id, name, athletes:[...]}]
    const [busy, setBusy] = useState(true);

    const [tplDraft, setTplDraft] = useState(null);
    const [slotDraft, setSlotDraft] = useState(null);
    const [publishDraft, setPublishDraft] = useState(null); // {template_id, from_date, to_date}
    const [activeSlot, setActiveSlot] = useState(null);
    const [closureDraft, setClosureDraft] = useState(null); // { date, note, isClosed }
    const [dedupeBusy, setDedupeBusy] = useState(false);

    // Tick every 30s so the "NOW" pill + "live" badge stay accurate without
    // hammering re-renders. Coaches typically leave the page open all day,
    // so the slot in progress needs to advance live.
    const [nowMs, setNowMs] = useState(() => Date.now());
    useEffect(() => {
        const id = window.setInterval(() => setNowMs(Date.now()), 30_000);
        return () => window.clearInterval(id);
    }, []);

    // Refs by slot_id so we can scrollIntoView the slot most relevant to
    // "now" once data finishes loading.
    const slotRefs = useRef({});


    // Bump this counter whenever a check-in / walk-in happens so the
    // RecentDeductionsPanel re-fetches its feed.
    const [deductionsBump, setDeductionsBump] = useState(0);

    useEffect(() => {
        if (loading) return;
        if (!user) { navigate("/", { replace: true }); return; }
        if (user.role !== "owner") navigate("/dashboard", { replace: true });
    }, [loading, user, navigate]);

    const weekFrom = useMemo(() => isoDate(weekStart), [weekStart]);
    const weekTo = useMemo(() => isoDate(addDays(weekStart, 6)), [weekStart]);

    const refreshAll = async () => {
        setBusy(true);
        try {
            const [t, s, leads, c] = await Promise.all([
                axios.get(`${API}/coach/schedule/templates`, { withCredentials: true }),
                axios.get(`${API}/coach/schedule/slots`, {
                    params: { from_date: weekFrom, to_date: weekTo },
                    withCredentials: true,
                }),
                axios.get(`${API}/coach/leads`, {
                    params: { status: "member" },
                    withCredentials: true,
                }),
                axios.get(`${API}/coach/schedule/closures`, {
                    params: { from_date: weekFrom, to_date: weekTo },
                    withCredentials: true,
                }),
            ]);
            setTemplates(t.data || []);
            setSlots(s.data || []);
            setClosures(c.data || []);
            const leadsList = (leads.data && (leads.data.leads || leads.data)) || [];
            setMembers(leadsList.map((m) => ({
                user_id: m.user_id,
                name: [m.parent_first_name, m.parent_last_name].filter(Boolean).join(" ") || m.email,
                email: m.email,
                athletes: m.athletes || [],
            })));
        } catch (e) {
            toast.error("Couldn't load schedule", { description: e?.response?.data?.detail });
        } finally {
            setBusy(false);
        }
    };

    // Owner-only cleanup: find slot rows that share the same
    // (date, start_time, label, category) and collapse them into one. Two-step
    // confirm so a stray click can't silently mutate the schedule.
    const cleanupDuplicates = async () => {
        setDedupeBusy(true);
        try {
            // 1) Dry-run first to show the user exactly what would change.
            const preview = await axios.post(
                `${API}/coach/schedule/dedupe-slots?dry_run=true`,
                {},
                { withCredentials: true },
            );
            const groups = preview.data?.duplicate_groups || 0;
            if (groups === 0) {
                toast.success("No duplicates found", { description: "Your schedule is already clean." });
                return;
            }
            const ok = window.confirm(
                `Found ${groups} duplicate time slot${groups === 1 ? "" : "s"} on the grid. ` +
                `Bookings (if any) will be moved to the canonical slot and the extras deleted. ` +
                `Proceed?`,
            );
            if (!ok) return;
            // 2) Real run.
            const r = await axios.post(
                `${API}/coach/schedule/dedupe-slots`,
                {},
                { withCredentials: true },
            );
            toast.success("Duplicates cleaned up", {
                description: `${r.data.slots_deleted} slot${r.data.slots_deleted === 1 ? "" : "s"} removed · ${r.data.bookings_moved} booking${r.data.bookings_moved === 1 ? "" : "s"} moved.`,
            });
            await refreshAll();
        } catch (e) {
            toast.error("Cleanup failed", { description: e?.response?.data?.detail });
        } finally {
            setDedupeBusy(false);
        }
    };

    useEffect(() => {
        if (user?.role === "owner") refreshAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, weekFrom, weekTo]);

    const slotsByDay = useMemo(() => {
        const out = Array.from({ length: 7 }, () => []);
        slots.forEach((sl) => {
            const d = new Date(`${sl.date}T00:00:00`);
            const idx = jsDayToMonIdx(d.getDay());
            out[idx].push(sl);
        });
        out.forEach((arr) => arr.sort((a, b) => a.start_time.localeCompare(b.start_time)));
        return out;
    }, [slots]);

    const closureByDate = useMemo(() => {
        const map = {};
        closures.forEach((c) => { map[c.date] = c; });
        return map;
    }, [closures]);

    // The slot the coach most likely wants to see RIGHT NOW. Priority:
    //   1. A slot in progress (between start and start+duration)
    //   2. The next upcoming slot today
    //   3. Otherwise, fall back to nothing (don't auto-scroll a non-current week)
    const nowSlot = useMemo(() => {
        if (!slots.length) return null;
        const today = new Date(nowMs);
        const todayIso = isoDate(today);
        const todays = slots.filter((s) => s.date === todayIso);
        if (!todays.length) return null;
        const live = todays.find((s) => isSlotInProgress(s, nowMs));
        if (live) return live;
        const upcoming = todays
            .filter((s) => slotStartMs(s) > nowMs)
            .sort((a, b) => slotStartMs(a) - slotStartMs(b));
        if (upcoming.length) return upcoming[0];
        // No upcoming — point at the most recent slot today instead.
        const past = todays
            .filter((s) => slotStartMs(s) <= nowMs)
            .sort((a, b) => slotStartMs(b) - slotStartMs(a));
        return past[0] || null;
    }, [slots, nowMs]);

    // 2026-06 (Sachin): pages must ALWAYS open at the top — the old
    // auto-scroll-to-current-slot landed coaches near the bottom late in the
    // day. Kept as a manual action: jumpToNow(), wired to a "Jump to now"
    // button in the header.
    const jumpToNow = () => {
        if (!nowSlot) return;
        const el = slotRefs.current[nowSlot.slot_id];
        el?.scrollIntoView({ behavior: "smooth", block: "center" });
    };

    const saveClosure = async () => {
        if (!closureDraft) return;
        try {
            if (closureDraft.isClosed) {
                await axios.put(
                    `${API}/coach/schedule/closures/${closureDraft.date}`,
                    { note: closureDraft.note || "" },
                    { withCredentials: true },
                );
                toast.success("Day marked closed");
            } else {
                await axios.delete(
                    `${API}/coach/schedule/closures/${closureDraft.date}`,
                    { withCredentials: true },
                );
                toast.success("Day re-opened");
            }
            setClosureDraft(null);
            refreshAll();
        } catch (e) {
            toast.error("Save failed", { description: e?.response?.data?.detail });
        }
    };

    const saveTemplate = async () => {
        if (!tplDraft) return;
        const isEdit = Boolean(tplDraft.template_id);
        const url = isEdit
            ? `${API}/coach/schedule/templates/${tplDraft.template_id}`
            : `${API}/coach/schedule/templates`;
        const method = isEdit ? "put" : "post";
        try {
            const payload = { ...tplDraft, category: tplDraft.category || null };
            const res = await axios[method](url, payload, { withCredentials: true });
            const savedTemplateId = res.data?.template_id;
            // Auto-publish 4 weeks of slots for brand-new templates so the
            // owner doesn't have to do a second step. Idempotent re-runs are
            // safe because the publish endpoint dedupes existing slots.
            if (!isEdit && savedTemplateId && payload.active !== false) {
                try {
                    const today = isoDate(new Date());
                    const fourWeeksOut = isoDate(addDays(new Date(), 27));
                    const pubRes = await axios.post(
                        `${API}/coach/schedule/templates/publish`,
                        { template_id: savedTemplateId, from_date: today, to_date: fourWeeksOut },
                        { withCredentials: true },
                    );
                    const n = pubRes.data?.created_count || 0;
                    toast.success(
                        `Template created · ${n} slot${n === 1 ? "" : "s"} published`,
                        { description: `Next 4 weeks (${today} → ${fourWeeksOut}). Use "Publish across dates" to extend further.` },
                    );
                } catch {
                    toast.success("Template created", {
                        description: "Couldn't auto-publish slots — use the Publish button on the card to add them.",
                    });
                }
            } else {
                toast.success(isEdit ? "Template updated" : "Template created");
            }
            setTplDraft(null);
            refreshAll();
        } catch (e) {
            toast.error("Save failed", { description: e?.response?.data?.detail });
        }
    };

    const deleteTemplate = async (t) => {
        if (!window.confirm(`Delete template "${t.label || formatTimeDisplay(t.start_time)}"?`)) return;
        try {
            await axios.delete(`${API}/coach/schedule/templates/${t.template_id}`, { withCredentials: true });
            toast.success("Template deleted");
            refreshAll();
        } catch (e) {
            toast.error("Delete failed", { description: e?.response?.data?.detail });
        }
    };

    const publishTemplate = async () => {
        if (!publishDraft) return;
        try {
            const r = await axios.post(
                `${API}/coach/schedule/templates/publish`,
                publishDraft,
                { withCredentials: true },
            );
            const created = r.data?.created_count || 0;
            const skipped = r.data?.skipped_closed_dates || [];
            if (created === 0 && skipped.length === 0) {
                // The publish ran but no new slots — all matching dates already had slots from this template.
                toast.info("Nothing to publish", {
                    description: "Every matching weekday in this range already has slots from this template. Pick a later end date to extend it.",
                });
            } else {
                const desc = skipped.length
                    ? `Skipped ${skipped.length} closed day${skipped.length === 1 ? "" : "s"}: ${skipped.slice(0, 3).join(", ")}${skipped.length > 3 ? "…" : ""}`
                    : undefined;
                toast.success(`Published ${created} slot(s)`, { description: desc });
            }
            // Jump the visible week to the publish start date so the new slots
            // are actually on screen — biggest "where did they go?" gotcha.
            try {
                if (publishDraft.from_date) {
                    const dStart = new Date(`${publishDraft.from_date}T00:00:00`);
                    setWeekStart(startOfWeekMon(dStart));
                }
            } catch { /* noop */ }
            setPublishDraft(null);
            refreshAll();
        } catch (e) {
            toast.error("Publish failed", { description: e?.response?.data?.detail });
        }
    };

    const saveSlot = async () => {
        if (!slotDraft) return;
        const isEdit = Boolean(slotDraft.slot_id);
        const url = isEdit
            ? `${API}/coach/schedule/slots/${slotDraft.slot_id}`
            : `${API}/coach/schedule/slots`;
        const method = isEdit ? "put" : "post";
        try {
            const payload = { ...slotDraft, category: slotDraft.category || null };
            await axios[method](url, payload, { withCredentials: true });
            toast.success(isEdit ? "Slot updated" : "Slot created");
            setSlotDraft(null);
            refreshAll();
        } catch (e) {
            toast.error("Save failed", { description: e?.response?.data?.detail });
        }
    };

    const deleteSlot = async (s) => {
        if (!window.confirm(`Delete this slot${s.booking_count ? ` and cancel ${s.booking_count} booking(s)` : ""}?`)) return;
        try {
            await axios.delete(`${API}/coach/schedule/slots/${s.slot_id}`, { withCredentials: true });
            toast.success("Slot deleted");
            setActiveSlot(null);
            refreshAll();
        } catch (e) {
            toast.error("Delete failed", { description: e?.response?.data?.detail });
        }
    };

    const assignAthlete = async (athleteId, userId) => {
        if (!activeSlot) return;
        try {
            await axios.post(
                `${API}/coach/schedule/bookings`,
                { slot_id: activeSlot.slot_id, athlete_id: athleteId, user_id: userId },
                { withCredentials: true },
            );
            toast.success("Assigned");
            const r = await axios.get(`${API}/coach/schedule/slots`, {
                params: { from_date: weekFrom, to_date: weekTo },
                withCredentials: true,
            });
            setSlots(r.data || []);
            setActiveSlot((r.data || []).find((s) => s.slot_id === activeSlot.slot_id) || null);
        } catch (e) {
            toast.error("Assign failed", { description: e?.response?.data?.detail });
        }
    };

    const cancelBooking = async (booking_id) => {
        try {
            await axios.delete(`${API}/coach/schedule/bookings/${booking_id}`, { withCredentials: true });
            toast.success("Booking cancelled");
            const r = await axios.get(`${API}/coach/schedule/slots`, {
                params: { from_date: weekFrom, to_date: weekTo },
                withCredentials: true,
            });
            setSlots(r.data || []);
            setActiveSlot((r.data || []).find((s) => s.slot_id === activeSlot.slot_id) || null);
        } catch (e) {
            toast.error("Cancel failed", { description: e?.response?.data?.detail });
        }
    };

    // Move a booked athlete from the active slot into a different slot in one
    // tap (no cancel-then-reassign). Session-neutral — sessions are only
    // deducted at check-in. Affects this occurrence only.
    const moveBooking = async (booking_id, targetSlotId) => {
        try {
            await axios.post(
                `${API}/coach/schedule/bookings/${booking_id}/move`,
                { target_slot_id: targetSlotId },
                { withCredentials: true },
            );
            toast.success("Moved to new slot");
            const r = await axios.get(`${API}/coach/schedule/slots`, {
                params: { from_date: weekFrom, to_date: weekTo },
                withCredentials: true,
            });
            setSlots(r.data || []);
            setActiveSlot((r.data || []).find((s) => s.slot_id === activeSlot.slot_id) || null);
        } catch (e) {
            toast.error("Move failed", { description: e?.response?.data?.detail });
        }
    };

    // Mark an existing booking present + decrement a session. Calls the
    // shared /coach/check-in endpoint and surfaces the decrement result via
    // a loud green/red toast so the coach can't miss whether a credit was
    // actually burned (the user's old CRM silently swallowed missed
    // decrements — this is the safety net against that).
    const markPresent = async (booking) => {
        try {
            const r = await axios.post(
                `${API}/coach/check-in`,
                { booking_id: booking.booking_id },
                { withCredentials: true },
            );
            toastDeduction(booking.athlete_name, r.data);
            const fresh = await axios.get(`${API}/coach/schedule/slots`, {
                params: { from_date: weekFrom, to_date: weekTo },
                withCredentials: true,
            });
            setSlots(fresh.data || []);
            setActiveSlot((fresh.data || []).find((s) => s.slot_id === activeSlot?.slot_id) || null);
            setDeductionsBump((n) => n + 1);
        } catch (e) {
            toast.error("Check-in failed", { description: e?.response?.data?.detail });
        }
    };

    // Drop-in walk-in: creates a NEW booking pre-checked-in for this slot,
    // then runs the same FIFO decrement as a normal check-in. Used by both
    // the slot modal's "+ Walk-in" picker and the floating live-slot pill.
    const addWalkIn = async (slotId, athleteId, athleteName) => {
        try {
            const r = await axios.post(
                `${API}/coach/walk-in`,
                { slot_id: slotId, athlete_id: athleteId },
                { withCredentials: true },
            );
            toastDeduction(athleteName, r.data);
            const fresh = await axios.get(`${API}/coach/schedule/slots`, {
                params: { from_date: weekFrom, to_date: weekTo },
                withCredentials: true,
            });
            setSlots(fresh.data || []);
            setActiveSlot((fresh.data || []).find((s) => s.slot_id === slotId) || null);
            setDeductionsBump((n) => n + 1);
        } catch (e) {
            toast.error("Walk-in failed", { description: e?.response?.data?.detail });
        }
    };

    if (loading || busy) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-brand-red" />
            </div>
        );
    }

    return (
        <div data-testid="coach-schedule" className="min-h-screen bg-black text-white">
            <div className="max-w-7xl mx-auto px-4 pt-28 pb-10">
                <OpsTabs />
                <div className="flex items-center gap-3 mb-6">
                    <Link to="/coach" className="text-zinc-200 hover:text-white inline-flex items-center gap-1">
                        <ArrowLeft className="w-4 h-4" /> Coach console
                    </Link>
                </div>

                <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-8">
                    <div>
                        <h1 className="text-4xl sm:text-5xl font-black tracking-tight">Schedule</h1>
                        <p className="mt-2 text-zinc-200">
                            Define recurring availability templates, then publish them out across weeks. Members book themselves into the open slots.
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <button
                            type="button"
                            data-testid="schedule-jump-to-now"
                            onClick={jumpToNow}
                            className="btn-ghost-tactical"
                            title="Scroll to the slot happening right now"
                        >
                            <Clock className="w-4 h-4" /> Jump to now
                        </button>
                        <button
                            type="button"
                            data-testid="schedule-clean-duplicates"
                            onClick={cleanupDuplicates}
                            disabled={dedupeBusy}
                            className="btn-ghost-tactical disabled:opacity-50"
                            title="Find slots that appear twice on the same date/time and merge them"
                        >
                            <Sparkles className="w-4 h-4" /> {dedupeBusy ? "Scanning…" : "Clean up duplicates"}
                        </button>
                        <button
                            type="button"
                            data-testid="schedule-add-template"
                            onClick={() => setTplDraft({ ...emptyTemplate })}
                            className="btn-ghost-tactical"
                        >
                            <Repeat className="w-4 h-4" /> New template
                        </button>
                        <button
                            type="button"
                            data-testid="schedule-add-slot"
                            onClick={() => setSlotDraft({ ...emptySlot })}
                            className="btn-tactical"
                        >
                            <CalendarPlus className="w-4 h-4" /> New one-off slot
                        </button>
                    </div>
                </div>

                {/* Sachin 2026-03-05: gym-timezone dropdown. Drives every
                    cutoff/reminder and the countdown parents see. */}
                <div className="mb-6">
                    <GymTimezoneSelect />
                </div>

                <div className="grid lg:grid-cols-12 gap-6">
                    {/* Templates rail */}
                    <div className="lg:col-span-4 border border-zinc-800 rounded-2xl bg-zinc-950/50">
                        <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between">
                            <h2 className="text-lg font-semibold">Recurring templates</h2>
                            <span className="text-xs text-zinc-300">{templates.length}</span>
                        </div>
                        <div data-testid="schedule-templates-list" className="divide-y divide-zinc-800 max-h-[70vh] overflow-y-auto">
                            {templates.length === 0 ? (
                                <p className="px-5 py-8 text-zinc-300 text-sm">
                                    No templates yet. Create one to define recurring availability (e.g. "Mon 6:00 AM Speed").
                                </p>
                            ) : templates.map((t) => {
                                const days = (t.days_of_week && t.days_of_week.length ? t.days_of_week : [t.day_of_week]).filter((d) => d !== undefined && d !== null);
                                const times = (t.start_times && t.start_times.length ? t.start_times : [t.start_time]).filter(Boolean);
                                const daysLabel = days.map((d) => DAY_LABELS[d]).join(" · ");
                                const timesLabel = times.length === 1
                                    ? formatTimeDisplay(times[0])
                                    : `${times.length} times · ${formatTimeDisplay(times[0])}–${formatTimeDisplay(times[times.length - 1])}`;
                                return (
                                <div
                                    key={t.template_id}
                                    data-testid={`schedule-template-${t.template_id}`}
                                    className="px-5 py-4 flex flex-col gap-2"
                                >
                                    <div className="flex items-center justify-between gap-3">
                                        <div className="min-w-0">
                                            <div className="text-sm text-zinc-200">
                                                {daysLabel} · {timesLabel} · {t.duration_minutes}min
                                            </div>
                                            <div className="font-semibold truncate">{t.label || "Small Group"}</div>
                                            <div className="text-xs text-zinc-300 mt-0.5">
                                                Cap {t.capacity} · cancel {t.cancel_window_hours}h
                                                {t.category ? ` · ${CATEGORY_LABELS[t.category]}` : ""}
                                                {t.active ? "" : " · inactive"}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-1 shrink-0">
                                            <button
                                                type="button"
                                                data-testid={`tpl-edit-${t.template_id}`}
                                                onClick={() => setTplDraft({
                                                    ...t,
                                                    label: t.label || "Small Group",
                                                    days_of_week: days,
                                                    start_times: times,
                                                    category: t.category || "",
                                                })}
                                                className="p-2 text-zinc-200 hover:text-white"
                                                title="Edit"
                                            >
                                                <Plus className="w-4 h-4 hidden" />
                                                Edit
                                            </button>
                                            <button
                                                type="button"
                                                data-testid={`tpl-delete-${t.template_id}`}
                                                onClick={() => deleteTemplate(t)}
                                                className="p-2 text-zinc-300 hover:text-brand-red"
                                                title="Delete"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        data-testid={`tpl-publish-${t.template_id}`}
                                        onClick={() => setPublishDraft({
                                            template_id: t.template_id,
                                            from_date: weekFrom,
                                            to_date: isoDate(addDays(weekStart, 27)),
                                        })}
                                        className="self-start inline-flex items-center gap-1.5 text-xs tracking-[0.15em] uppercase px-3 py-2 border border-brand-red text-brand-red bg-brand-red/5 hover:bg-brand-red hover:text-white transition-colors"
                                    >
                                        <CalendarPlus className="w-3.5 h-3.5" />
                                        Publish to schedule
                                    </button>
                                </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Week grid */}
                    <div className="lg:col-span-8 space-y-4">
                        <RecentDeductionsPanel bump={deductionsBump} />
                        <div className="border border-zinc-800 rounded-2xl bg-zinc-950/50">
                        <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    data-testid="schedule-week-prev"
                                    onClick={() => setWeekStart((d) => addDays(d, -7))}
                                    className="p-2 hover:text-brand-red"
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setWeekStart(startOfWeekMon(new Date()))}
                                    className="text-xs px-3 py-1 rounded-full border border-zinc-700 hover:border-brand-red hover:text-brand-red"
                                >
                                    This week
                                </button>
                                <button
                                    type="button"
                                    data-testid="schedule-week-next"
                                    onClick={() => setWeekStart((d) => addDays(d, 7))}
                                    className="p-2 hover:text-brand-red"
                                >
                                    <ChevronRight className="w-4 h-4" />
                                </button>
                                <h2 className="ml-2 text-lg font-semibold">{weekRangeLabel(weekStart)}</h2>
                            </div>
                            <span className="text-xs text-zinc-300">{slots.length} slot(s)</span>
                        </div>

                        <div className="grid grid-cols-7 divide-x divide-zinc-800">
                            {DAY_LABELS.map((label, idx) => {
                                const day = addDays(weekStart, idx);
                                const dayIso = isoDate(day);
                                const closure = closureByDate[dayIso];
                                return (
                                    <div key={label} className="min-h-[20rem] flex flex-col">
                                        <button
                                            type="button"
                                            data-testid={`day-header-${dayIso}`}
                                            onClick={() => setClosureDraft({
                                                date: dayIso,
                                                note: closure?.note || "",
                                                isClosed: !!closure,
                                                originallyClosed: !!closure,
                                            })}
                                            className={`px-3 py-2 border-b sticky top-0 backdrop-blur text-left transition-colors ${
                                                closure
                                                    ? "border-brand-red bg-brand-red/10 hover:bg-brand-red/20"
                                                    : "border-zinc-800 bg-zinc-950/80 hover:bg-zinc-900"
                                            }`}
                                        >
                                            <div className="text-xs text-zinc-300 uppercase tracking-wider">{label}</div>
                                            <div className="text-sm font-semibold flex items-center gap-1">
                                                {day.getDate()}
                                                {closure && <span className="text-[10px] text-brand-red ml-1">CLOSED</span>}
                                            </div>
                                        </button>
                                        <div className="flex-1 px-2 py-2 space-y-2">
                                            {closure ? (
                                                <div className="text-[11px] text-brand-red bg-brand-red/5 border border-brand-red/30 rounded-lg px-2 py-2 leading-snug">
                                                    <div className="font-semibold uppercase tracking-wider text-[10px] mb-1">Gym closed</div>
                                                    <div className="text-zinc-200">{closure.note || "(no reason given)"}</div>
                                                </div>
                                            ) : slotsByDay[idx].length === 0 ? (
                                                <div className="text-[11px] text-zinc-200 px-1">—</div>
                                            ) : slotsByDay[idx].map((s) => {
                                                const full = s.booking_count >= s.capacity;
                                                const live = isSlotInProgress(s, nowMs);
                                                const upNext = !live && isSlotUpNext(s, nowMs, 30);
                                                return (
                                                    <button
                                                        key={s.slot_id}
                                                        ref={(el) => { if (el) slotRefs.current[s.slot_id] = el; }}
                                                        type="button"
                                                        data-testid={`coach-slot-${s.slot_id}`}
                                                        data-live={live ? "1" : "0"}
                                                        onClick={() => setActiveSlot(s)}
                                                        className={`w-full text-left rounded-lg border px-2 py-1.5 transition relative ${
                                                            live
                                                                ? "border-brand-red bg-brand-red/15 shadow-[0_0_24px_-6px] shadow-brand-red/70 ring-1 ring-brand-red/60"
                                                                : upNext
                                                                    ? "border-amber-500/70 bg-amber-500/10 hover:border-amber-400"
                                                                    : full
                                                                        ? "border-brand-navy-700/60 bg-brand-navy-900/40 hover:bg-brand-navy-800/40"
                                                                        : "border-zinc-700 bg-zinc-900 hover:border-brand-red"
                                                        }`}
                                                    >
                                                        {live && (
                                                            <span
                                                                data-testid={`slot-now-pill-${s.slot_id}`}
                                                                className="absolute -top-2 -right-2 inline-flex items-center gap-1 text-[9px] font-bold tracking-[0.2em] uppercase px-1.5 py-0.5 bg-brand-red text-white rounded-sm shadow-lg animate-pulse"
                                                            >
                                                                ● NOW
                                                            </span>
                                                        )}
                                                        {upNext && (
                                                            <span
                                                                data-testid={`slot-next-pill-${s.slot_id}`}
                                                                className="absolute -top-2 -right-2 inline-flex items-center text-[9px] font-bold tracking-[0.2em] uppercase px-1.5 py-0.5 bg-amber-500 text-black rounded-sm"
                                                            >
                                                                NEXT
                                                            </span>
                                                        )}
                                                        <div className="text-sm font-semibold">{formatTimeDisplay(s.start_time)}</div>
                                                        <div className="text-[11px] text-zinc-200 truncate">
                                                            {s.label || (s.category ? CATEGORY_LABELS[s.category] : `${s.duration_minutes}min`)}
                                                        </div>
                                                        <SlotAttendees bookings={s.bookings} />
                                                        <div className="text-[11px] mt-0.5">
                                                            <span className={full ? "text-brand-navy-300" : "text-zinc-300"}>
                                                                {s.booking_count}/{s.capacity}
                                                            </span>
                                                        </div>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    </div>
                </div>
            </div>

            {tplDraft && (
                <TemplateModal
                    draft={tplDraft}
                    setDraft={setTplDraft}
                    onSave={saveTemplate}
                    onClose={() => setTplDraft(null)}
                />
            )}
            {closureDraft && (
                <CloseDayModal
                    draft={closureDraft}
                    setDraft={setClosureDraft}
                    onSave={saveClosure}
                    onClose={() => setClosureDraft(null)}
                />
            )}
            {slotDraft && (
                <SlotModal
                    draft={slotDraft}
                    setDraft={setSlotDraft}
                    onSave={saveSlot}
                    onClose={() => setSlotDraft(null)}
                />
            )}
            {publishDraft && (
                <PublishModal
                    draft={publishDraft}
                    setDraft={setPublishDraft}
                    onSave={publishTemplate}
                    onClose={() => setPublishDraft(null)}
                />
            )}
            {activeSlot && (
                <SlotBookingsModal
                    slot={activeSlot}
                    allSlots={slots}
                    members={members}
                    isLive={isSlotInProgress(activeSlot, nowMs)}
                    onAssign={assignAthlete}
                    onCancel={cancelBooking}
                    onMove={moveBooking}
                    onMarkPresent={markPresent}
                    onWalkIn={addWalkIn}
                    onDelete={() => deleteSlot(activeSlot)}
                    onEdit={() => { setSlotDraft({ ...activeSlot, category: activeSlot.category || "" }); setActiveSlot(null); }}
                    onClose={() => setActiveSlot(null)}
                    onBookingsChanged={async () => {
                        // Sachin 2026-03-06 — after an inline +/- session
                        // adjust, refresh the slot list so other bookings
                        // that share the same parent's pack pool reflect
                        // the new balance in real time.
                        try {
                            const r = await axios.get(`${API}/coach/schedule/slots`, {
                                params: { from_date: weekFrom, to_date: weekTo },
                                withCredentials: true,
                            });
                            setSlots(r.data || []);
                            setActiveSlot((r.data || []).find((s) => s.slot_id === activeSlot.slot_id) || null);
                        } catch (_e) { /* toast already fired inside the modal */ }
                    }}
                />
            )}
            {nowSlot && isSlotInProgress(nowSlot, nowMs) && !activeSlot && (
                <FloatingNowPill
                    slot={nowSlot}
                    onOpen={() => setActiveSlot(nowSlot)}
                />
            )}
        </div>
    );
}

/**
 * SlotAttendees — inline list of athlete first names inside a slot card on
 * the coach weekly grid. Sachin (2026-02-13): "I want coaches to see who's
 * coming without having to click each slot."
 *
 * Behavior:
 *  - Shows up to 3 names; the rest collapse into a "+N" pill.
 *  - Disambiguates duplicates: if 2+ athletes share a first name, that name
 *    is rendered as "First L." (first name + last-initial).
 *  - Marks checked-in athletes with a ✓ prefix so the coach can scan
 *    attendance state at a glance.
 *  - No-show / cancelled / late-cancel bookings are excluded (they're not
 *    coming in).
 *  - Renders nothing when bookings is empty so the card stays compact.
 *
 * Props:
 *   bookings: Array<{ athlete_name?: string, status?: string }>
 */
function SlotAttendees({ bookings }) {
    if (!bookings || bookings.length === 0) return null;
    const active = bookings.filter(
        (b) => b && b.status !== "no_show" && b.status !== "cancelled",
    );
    if (active.length === 0) return null;

    // Dedupe-aware display name: if first name collides with another booking
    // in this slot, append last initial.
    const firstCounts = {};
    active.forEach((b) => {
        const first = (b.athlete_name || "").trim().split(/\s+/)[0] || "—";
        firstCounts[first] = (firstCounts[first] || 0) + 1;
    });
    const display = active.map((b) => {
        const parts = (b.athlete_name || "").trim().split(/\s+/);
        const first = parts[0] || "—";
        const lastInitial = (parts[1] || "")[0];
        const name = firstCounts[first] > 1 && lastInitial ? `${first} ${lastInitial}.` : first;
        return {
            name,
            // Backend has two equivalent statuses for "they're here" —
            // "checked_in" (quick check-in / walk-in / auto) and "attended"
            // (manual reconciliation logs). UI must treat both as the ✓ state
            // so a checked-in athlete is never rendered as "still scheduled".
            checkedIn: b.status === "checked_in" || b.status === "attended",
        };
    });

    const visible = display.slice(0, 3);
    const overflow = display.length - visible.length;

    return (
        <div
            data-testid="slot-attendees"
            className="mt-1 -mx-0.5 flex flex-wrap gap-x-1 gap-y-0.5 text-[10px] leading-tight"
        >
            {visible.map((a, i) => (
                <span
                    key={i}
                    title={a.name}
                    className={`px-1 rounded-sm whitespace-nowrap ${
                        a.checkedIn
                            ? "text-emerald-300 bg-emerald-500/10"
                            : "text-zinc-100 bg-white/5"
                    }`}
                >
                    {a.checkedIn ? "✓ " : ""}
                    {a.name}
                </span>
            ))}
            {overflow > 0 && (
                <span
                    data-testid="slot-attendees-overflow"
                    className="px-1 rounded-sm whitespace-nowrap text-zinc-300 bg-white/5"
                >
                    +{overflow}
                </span>
            )}
        </div>
    );
}

